import styled from '@emotion/styled';

import { Highlight } from '@/components/common/Accent';
import { screen } from '@/components/common/breakpoints';
import { ArrowButtonLink } from '@/components/common/Button';
import { PrimaryButton, SecondaryButton } from '@/components/common/Buttons';
import { HeadingLevelProvider } from '@/components/common/Heading/context';
import {
  Heading,
  HeadingLevel,
  List,
  Paragraph,
} from '@/components/common/MarkUp';
import { Tooltip } from '@/components/common/Tooltip';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { bodyMedium, headlineSmall } from '@/styles/typography';

import type { DecorativeColourTokenType } from '@/types/colours';
import type { SanityProgrammeType } from '@/types/sanity';
import type { Locale } from '@/types/shared';
import type { ReactNode } from 'react';

const levelContext =
  'Most qualifications have a difficulty level. There are 9 qualification levels. The higher the level, the more difficult the qualification is.';

const candidateCtaLink = (locale: Locale, override: string) => {
  if (override) {
    return override;
  }

  switch (locale) {
    case 'en-GB':
      return 'https://platform.multiverse.io/candidate/welcome';
    case 'en-US':
      return 'https://platform.multiverse.io/us/candidate/welcome';
    default:
      return '';
  }
};

export interface ProgrammeDetailsProps {
  programme: SanityProgrammeType;
  locale: Locale;
}

const ProgrammeDetails = ({ locale, programme }: ProgrammeDetailsProps) => {
  return (
    <Section backgroundColour="--background-warm-base">
      <GridWrapper>
        <HeadingLevelProvider level={1}>
          <Heading size="display-md">{programme.name}</Heading>
          <Subheading size="body-md">{programme.introduction}</Subheading>
          <OverviewContainer>
            <HeadingLevel>
              <Heading size="subheadline">Overview</Heading>

              <PortableText value={programme.detailedOverview} />
            </HeadingLevel>
          </OverviewContainer>
          <CardContainer>
            <HeadingLevel>
              <HeadingLevel>
                <Highlights>
                  <HighlightText>
                    <Heading size="caption-sm">Duration</Heading>
                    <Paragraph size="body-md">{programme.duration}</Paragraph>
                  </HighlightText>
                  <HighlightText>
                    <Heading size="caption-sm">Price</Heading>
                    <Paragraph size="body-md">
                      <TextHighlight colour={programme?.accentColour?.token}>
                        Free tuition
                      </TextHighlight>
                    </Paragraph>
                  </HighlightText>
                  {!!programme.stage && (
                    <HighlightText>
                      <Heading size="caption-sm">Stage</Heading>
                      <Paragraph size="body-md">{programme.stage}</Paragraph>
                    </HighlightText>
                  )}
                </Highlights>
                {(!!programme.level || !!programme.qualificationType) && (
                  <Highlights>
                    {!!programme.level && (
                      <HighlightText>
                        <Heading size="caption-sm">
                          Level
                          <ToolTip
                            direction="right"
                            content={levelContext}
                            id="programme-level-tooltip"
                          />
                        </Heading>
                        <Paragraph size="body-md">{programme.level}</Paragraph>
                        <MobileOnlyContainer>
                          <Paragraph size="body-md">{levelContext}</Paragraph>
                        </MobileOnlyContainer>
                      </HighlightText>
                    )}
                    {!!programme.qualificationType && (
                      <HighlightText>
                        <Heading size="caption-sm">Qualification</Heading>
                        <Paragraph size="body-md">
                          {programme.qualificationType}
                        </Paragraph>
                      </HighlightText>
                    )}
                  </Highlights>
                )}

                <SummaryBlock>
                  <Heading size="caption-sm">
                    <TextHighlight colour={programme?.accentColour?.token}>
                      Who it&apos;s for
                    </TextHighlight>
                  </Heading>
                  <Paragraph size="body-md">{programme.whoItsFor}</Paragraph>
                </SummaryBlock>
                <SummaryBlock>
                  <Heading size="caption-sm">
                    To apply{' '}
                    <TextHighlight colour={programme?.accentColour?.token}>
                      you&apos;ll need
                    </TextHighlight>
                  </Heading>
                  <List>
                    {programme.requirements.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </List>
                </SummaryBlock>
                {!!programme.qualificationsReceived && (
                  <SummaryBlock>
                    <Heading size="caption-sm">
                      <TextHighlight colour={programme?.accentColour?.token}>
                        Qualifications
                      </TextHighlight>{' '}
                      Received
                    </Heading>
                    <List>
                      {programme.qualificationsReceived.map((q) => (
                        <li key={q}>{q}</li>
                      ))}
                    </List>
                  </SummaryBlock>
                )}
              </HeadingLevel>
              <CTAContainer>
                <CTA>
                  <Paragraph size="body-md-semi-bold">Employer?</Paragraph>
                  <SecondaryButton
                    componentTheme="light"
                    href={`/${locale}/employer-contact-form`}
                    className={
                      programme.enquireTrackingTag?.blockName &&
                      `tracking-${programme.enquireTrackingTag.blockName}__${programme.enquireTrackingTag.blockAction}`
                    }
                  >
                    {employersCtaLabel(locale)}
                  </SecondaryButton>
                </CTA>
                <CTA>
                  <Paragraph size="body-md-semi-bold">
                    Starting your career?
                  </Paragraph>
                  <PrimaryButton
                    componentTheme="light"
                    iconPosition="right"
                    href={candidateCtaLink(
                      locale,
                      programme.getStartedOverride,
                    )}
                    className={
                      programme.getStartedTrackingTag?.blockName &&
                      `tracking-${programme.getStartedTrackingTag.blockName}__${programme.getStartedTrackingTag.blockAction}`
                    }
                  >
                    Get started
                  </PrimaryButton>
                </CTA>
              </CTAContainer>
              {!!programme.helperText && (
                <HelperText size="body-sm">{programme.helperText}</HelperText>
              )}
            </HeadingLevel>
          </CardContainer>
        </HeadingLevelProvider>
      </GridWrapper>
    </Section>
  );
};

const employersCtaLabel = (locale: Locale) => {
  switch (locale) {
    case 'en-GB':
      return 'Enquire now';
    case 'en-US':
      return 'Inquire now';
    default:
      return 'Enquire now';
  }
};

interface HighlightProps {
  colour?: DecorativeColourTokenType;
  children: ReactNode;
}

const TextHighlight = ({
  children,
  colour = '--decorative-eclipse',
}: HighlightProps) => {
  return (
    <HighlightedTextContainer>
      {children}
      <HighlightContainer colour={colour}>
        <Highlight variant="straight" />
      </HighlightContainer>
    </HighlightedTextContainer>
  );
};

const HighlightContainer = styled.span<{ colour: DecorativeColourTokenType }>`
  position: absolute;
  color: ${({ colour }) => `var(${colour})`};
  display: flex;
  pointer-events: none;
  height: 100%;
  top: 0px;
  right: -4px;
  width: calc(100% + 8px);
  z-index: -1;
`;

const HelperText = styled(Paragraph)`
  padding-top: var(--spacing-small);
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: auto;
  gap: var(--spacing-large);
  grid-template-areas:
    'header'
    'subheading'
    'card'
    'overview';

  position: relative;

  padding: var(--spacing-1200) 0 var(--spacing-800);

  ${screen.sm} {
    padding: var(--spacing-1200) 0 var(--spacing-800);
  }

  ${screen.md} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto 1fr;
    grid-gap: var(--spacing-large) var(--spacing-xx-large);
    grid-template-areas:
      'header header'
      'subheading card'
      'overview card';

    overflow: hidden;
    padding: calc(var(--spacing-2000) + var(--spacing-400)) 0 var(--spacing-800);
  }

  ${screen.lg} {
    padding: calc(var(--spacing-2000) + var(--spacing-400)) 0
      var(--spacing-1200);
  }

  h1 {
    grid-area: header;
    -ms-grid-row: 1;
    -ms-grid-column: 1;
    -ms-grid-column-span: 3;
  }

  display: -ms-grid;
  -ms-grid-columns: 1fr var(--spacing-xx-large) 1fr;
  -ms-grid-rows: 100px 80px auto 80px 1fr;
`;

const Subheading = styled(Paragraph)`
  grid-area: subheading;
  -ms-grid-row: 3;
  -ms-grid-column: 1;
  ${headlineSmall};
  font-weight: var(--font-weight-normal);
`;

const OverviewContainer = styled.div`
  grid-area: overview;
  -ms-grid-row: 5;
  -ms-grid-column: 1;

  padding-bottom: var(--spacing-small);
  padding-top: var(--spacing-small);

  h2 {
    margin-bottom: var(--spacing-large);
  }

  p {
    ${bodyMedium};
    margin-top: var(--spacing-small);
    color: inherit;
  }

  ${screen.md} {
    min-height: 780px;
    padding-bottom: 0;
  }
`;

const CardContainer = styled.div`
  grid-area: card;
  -ms-grid-row: 3;
  -ms-grid-row-span: 3;
  -ms-grid-column: 3;

  background: var(--surface-subtle);
  padding: var(--spacing-xx-large);
  height: fit-content;
  z-index: 2;
  box-shadow: var(--shadow-subtle);

  h2 {
    margin-bottom: var(--spacing-small);
  }

  h3 {
    font-weight: var(--font-weight-semi-bold);
  }

  ${screen.sm} {
    border-radius: var(--radius-s);
  }
`;

const Highlights = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: var(--spacing-300);

  ${screen.sm} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const HighlightText = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-50);
  &:not(:last-of-type) {
    margin-bottom: var(--spacing-large);
  }
  ${screen.sm} {
    flex-basis: 33.3%;
    &:not(:last-of-type),
    & {
      margin-bottom: 0;
    }
  }

  h2 {
    margin-bottom: var(--spacing-small);
    ${screen.sm} {
      margin-bottom: var(--spacing-xx-small);
    }
  }
`;

const SummaryBlock = styled.div`
  margin-top: var(--spacing-large);

  h3 {
    margin-bottom: var(--spacing-xx-small);
  }
  h2 {
    position: relative;
  }

  ul {
    list-style: disc;

    padding-top: var(--spacing-small);
  }

  li {
    &::marker {
      color: var(--decorative-ultraviolet);
    }

    ${bodyMedium};
    margin-bottom: var(--spacing-xx-small);
  }
`;

const CTAContainer = styled.div`
  background: var(--surface-primary-subtle);
  padding: var(--spacing-large);
  margin-top: var(--spacing-large);
  display: flex;
  gap: var(--spacing-small);
  flex-direction: column;
  border-radius: var(--radius-s);

  ${Heading} {
    margin-bottom: var(--spacing-xx-large);
  }

  ${screen.sm} {
    flex-direction: row;
    gap: var(--spacing-800);

    > * {
      margin-bottom: 0;
    }
    ${Heading} {
      margin-bottom: var(--spacing-small);
    }
  }
  ${screen.md} {
    flex-direction: column;
    gap: var(--spacing-500);
  }

  ${screen.lg} {
    flex-direction: row;
  }
`;

const CTA = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xx-small);

  ${screen.sm} {
    ${ArrowButtonLink} {
      margin-top: 10px;
    }
  }
`;

const HighlightedTextContainer = styled.span`
  width: max-content;
  position: relative;
  display: inline-flex;
`;

const MobileOnlyContainer = styled.div`
  display: block;
  margin-top: var(--spacing-small);

  ${screen.sm} {
    display: none;
  }
`;

const ToolTip = styled(Tooltip)`
  display: none;

  ${screen.sm} {
    text-transform: initial;
    letter-spacing: initial;
    display: inline-block;
    position: absolute;
    top: -3px;
    margin-left: 3px;
  }
`;

export { ProgrammeDetails };
